import { FC, useCallback, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import isNil from 'lodash.isnil'

import {
  SidePanelHeader,
  SidePanelObject,
  SidePanelElementProps,
  useSidePanelContext,
} from '@clara/layout-next'
import { Button, PatternField } from '@clara/ui'
import { Translation, useT } from '@clara/locale'
import { Dict } from '@clara/utils'

import { getErrorHelperText } from '@/utils/form'
import { useSnackbar } from '@/utils/use-snackbar'
import { CpfMask, BrPhoneMask } from '@/utils/string'

import { getBankInfoSchema } from '../use-bankinfo-schema'
import {
  BankInfoResponse,
  PixPayload,
  postBankInfo,
  PixAccountType,
} from '../../../api/bank-info'
import { getSuccessBankInfoSidePanel } from '../success'
import { useReimbursementsInProgress } from '@/features/reimbursements/api/use-reimbursements-inprogress'

type Props = {
  getBankInfo: () => Promise<BankInfoResponse>
  bankInfo?: BankInfoResponse
}

type Option = {
  name: string
  mask?: string
  component: (props: any) => JSX.Element
  key: string
}

const options: Option[] = [
  {
    name: 'cpf',
    key: 'CPF',
    mask: CpfMask,
    component: (props: any) => <PatternField {...props} />,
  },
  {
    name: 'email',
    key: 'EMAIL',
    component: (props: any) => <TextField {...props} />,
  },
  {
    name: 'phone',
    key: 'PHONE',
    component: (props: any) => <PatternField {...props} />,
    mask: BrPhoneMask,
  },
  {
    name: 'randomKey',
    key: 'RANDOM_KEY',
    component: (props: any) => <TextField {...props} />,
  },
]

const initialValues = {
  selectedMethod: '',
  randomKey: '',
  phone: '',
  cpf: '',
  email: '',
}

const PixSidePanel: FC<SidePanelElementProps & Props> = ({
  getBankInfo,
  bankInfo,
}) => {
  const { t } = useT('reimbursements')
  const sidePanel = useSidePanelContext()
  const schema = getBankInfoSchema(t, 'pix')
  const showSnackbar = useSnackbar()
  const [saveButtonIsLoading, setSaveButtonIsLoading] = useState(false)
  const cpfRef = useRef<HTMLInputElement>(null)

  const defaultSelectedMethod = options.find(
    (item) => item.key === bankInfo?.accountSubType,
  )?.name as string

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues:
      bankInfo?.accountType === 'PIX' && defaultSelectedMethod
        ? {
            selectedMethod: options.find(
              (item) => item.key === bankInfo?.accountSubType,
            )?.name,
            [defaultSelectedMethod]: bankInfo?.accountNumber ?? '',
          }
        : initialValues,
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })

  const selectedMethod = watch('selectedMethod')

  const updateBankInfo = useCallback(
    async (data: Dict<string>) => {
      const payload: PixPayload = {
        accountType: 'PIX',
        accountNumber: data[data.selectedMethod],
        accountSubType: options.find(
          (item) => item.name === data.selectedMethod,
        )?.key as PixAccountType,
      }
      setSaveButtonIsLoading(true)
      try {
        const res = await postBankInfo(payload, bankInfo?.uuid)
        if (!isNil(res)) {
          sidePanel.onOpen(
            getSuccessBankInfoSidePanel({
              method: 'PIX',
            }),
            0,
          )
          getBankInfo()
        }
      } catch (error) {
        // TODO: manage endpoint error codes
        showSnackbar(t('bankinfo.update.error'), {
          variant: 'error',
        })
      } finally {
        setSaveButtonIsLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bankInfo],
  )

  const { onSubmit } = useReimbursementsInProgress(updateBankInfo)

  return (
    <Stack>
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.v3.content.medium_emphasis,
          marginBottom: theme.spacing(3),
        })}
      >
        {t('pix.settings.description')}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name={'selectedMethod'}
          render={({ field, fieldState: { error } }) => (
            <RadioGroup {...field}>
              {options.map((option) => (
                <Box key={`radiogroup-option-${option.name}`}>
                  <FormControlLabel
                    value={option.name}
                    control={<Radio />}
                    label={t(`pix.fields.${option.name}.title`)}
                    sx={{ marginBottom: 2 }}
                  />
                  {selectedMethod === option.name && (
                    <>
                      <Controller
                        control={control}
                        name={option.name}
                        render={({ field, fieldState: { error } }) =>
                          option.component({
                            ...field,
                            ref: cpfRef,
                            label:
                              t('pix.fields.add') +
                              ' ' +
                              t(`pix.fields.${option.name}.title`),
                            variant: 'outlined',
                            error: error != null,
                            helperText: error && getErrorHelperText(t, error),
                            size: 'small',
                            fullWidth: true,
                            autoFocus: true,
                            sx: { marginBottom: 2 },
                            ...(option.mask && {
                              inputProps: {
                                format: option.mask,
                                valueIsNumericString: true,
                                type: 'tel',
                              },
                            }),
                          })
                        }
                      />
                      <Divider sx={{ marginBottom: 1 }} />
                    </>
                  )}
                </Box>
              ))}
            </RadioGroup>
          )}
        />

        <Stack direction={'column'} gap={3} mb={7}>
          <Stack
            direction="row"
            gap={2}
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              position: 'absolute',
              paddingTop: theme.spacing(2),
              paddingBottom: theme.spacing(3),
              left: theme.spacing(3),
              right: theme.spacing(3),
              bottom: 0,
              backgroundColor: theme.palette.common.white,
              zIndex: 1,
            })}
          >
            <Button
              type={'submit'}
              sx={{ width: '208px' }}
              isLoading={saveButtonIsLoading}
              isDisabled={!isDirty}
            >
              {t('bankinfo.sidepanel.save')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  )
}

export const getPixSidePanel = ({ getBankInfo, bankInfo }: Props) =>
  [
    {
      children: [
        {
          element: (
            <PixSidePanel getBankInfo={getBankInfo} bankInfo={bankInfo} />
          ),
          header: (
            <Translation ns="reimbursements">
              {(t) => <SidePanelHeader headline={t('pix.settings.title')} />}
            </Translation>
          ),
        },
      ],
    },
  ] as SidePanelObject[]
